import type { IAlertConfig, TypeAlertBuild } from './types';

import { merge } from '../../helper';

import addClass from '../../general/class/addClass';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';

import exExternal from '../../external';
import * as exElement from '../../utils/element';
import * as exIcon from '../../utils/icon';

import { createDismissButton } from './utils';

// ----------------------------------------------------------------------------

function create(config: IAlertConfig = {}): TypeAlertBuild {
    const defConfig: IAlertConfig = {
        bootstrap: {
            enabled: true,
        },
    };
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const elements = {} as TypeAlertBuild;

    // Create element (alert)
    const elAlert = exElement.create({
        merge: defConfig,
        class: {
            new: 'alert',
        },
        attr: {
            role: 'alert',
        },
    });
    elements.element = elAlert;

    // ------------------------------------------------------------------------

    // Settings
    settings(elements, defConfig);

    // ------------------------------------------------------------------------

    // If using bootstrap
    if (defConfig.bootstrap?.enabled === true) {
        // Bootstrap (alert)
        const bsAlert = new exExternal.bootstrap.Alert(elements.element);
        elements.bs = bsAlert;
    }

    // ------------------------------------------------------------------------

    // Return
    return elements;
}

function settings(elements: TypeAlertBuild, config: IAlertConfig = {}): void {
    // If using type
    if (typeof config.type === 'string') {
        addClass(elements.element, 'alert-' + config.type);
    }

    // If using hide
    if (config.hide === true) {
        elements.element.style.display = 'none';
    }

    // If using fade
    if (config.fade === true) {
        addClass(elements.element, 'fade show');
    }

    // ------------------------------------------------------------------------

    // If using alert header
    if (!isEmptyObject(config.alertHeader)) {
        // Create element (alert header)
        const elAlertHeader = exElement.create({
            merge: config.alertHeader,
            class: {
                new: 'alert-header',
            },
        });

        if (config.alertHeader?.icon != null || config.alertHeader?.title != null) {
            addClass(elAlertHeader, 'alert-header-layout');

            if (config.alertHeader?.icon != null) {
                const elAlertHeaderIcon = exElement.create({
                    class: {
                        new: 'alert-header-icon',
                    },
                });
                elAlertHeaderIcon.appendChild(exIcon.create(config.alertHeader.icon));
                elAlertHeader.appendChild(elAlertHeaderIcon);
            }

            if (config.alertHeader?.title != null) {
                const elAlertHeaderTitle = exElement.create({
                    merge:
                        typeof config.alertHeader.title === 'string'
                            ? { html: config.alertHeader.title }
                            : config.alertHeader.title,
                    class: {
                        new: 'alert-header-title',
                    },
                });
                elAlertHeader.appendChild(elAlertHeaderTitle);
            }
        }

        elements.element.appendChild(elAlertHeader);
        elements.header = elAlertHeader;
    }

    // ------------------------------------------------------------------------

    // If using alert body
    if (!isEmptyObject(config.alertBody)) {
        // Create element (alert body)
        const elAlertBody = exElement.create({
            merge: config.alertBody,
            class: {
                new: 'alert-body',
            },
        });
        elements.element.appendChild(elAlertBody);
        elements.body = elAlertBody;

        // If using scroll
        if (config.alertBody?.scroll === true) {
            addClass(elAlertBody, 'alert-scroll');
        }
    }

    // ------------------------------------------------------------------------

    // If using allow dismiss
    if (config.allowDismiss === true || !isEmptyObject(config.allowDismiss)) {
        const dismissConfig = typeof config.allowDismiss === 'object' ? config.allowDismiss : {};

        addClass(elements.element, 'alert-dismissible');

        // Create element (button dismiss)
        const buttonDismiss = createDismissButton(elements.element, dismissConfig);

        //
        if (elements.header?.classList.contains('alert-header-layout')) {
            addClass(elements.element, 'alert-dismissible-header');
            addClass(buttonDismiss.element, 'alert-header-dismiss');
            elements.header.appendChild(buttonDismiss.element);
        }
        //
        else {
            elements.element.appendChild(buttonDismiss.element);
        }

        elements.dismiss = buttonDismiss;
    }
}

// ----------------------------------------------------------------------------

export { create };
